"use client";
import { useEffect, useState } from "react";

import { BreadcrumbSchema } from "@/components/JsonLd";
import PageShell from "@/components/PageShell";

type Check = { name: string; ok: boolean; latency_ms: number | null; error: string | null };
type Version = { version: string; git_sha: string; deployed_at: string; claude_model: string; environment: string } | null;
type StatusPayload = {
  status: "ok" | "degraded" | "unreachable";
  checks: Check[];
  timestamp: string;
  version: Version;
};

const POLL_MS = 30_000;

function statusColor(s: StatusPayload["status"]): string {
  if (s === "ok") return "var(--green)";
  if (s === "degraded") return "var(--amber)";
  return "var(--red)";
}

function statusLabel(s: StatusPayload["status"]): string {
  if (s === "ok") return "All systems operational";
  if (s === "degraded") return "Degraded performance";
  return "Unable to reach the API";
}

function formatDeployedAgo(iso: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? "" : "s"} ago`;
  } catch {
    return iso;
  }
}

export default function StatusPage() {
  const [payload, setPayload] = useState<StatusPayload | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      setFetching(true);
      try {
        const res = await fetch("/api/status", { cache: "no-store" });
        const data = (await res.json()) as StatusPayload;
        if (!cancelled) {
          setPayload(data);
          setLastChecked(new Date());
        }
      } catch {
        if (!cancelled) {
          setPayload({ status: "unreachable", checks: [], timestamp: new Date().toISOString(), version: null });
          setLastChecked(new Date());
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    }
    check();
    const id = setInterval(check, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (<>
    <BreadcrumbSchema
      items={[
        { name: "Home", url: "/" },
        { name: "System status", url: "/status" },
      ]}
    />
    <PageShell>
    <section style={{padding:"72px clamp(20px,4vw,52px) 32px",textAlign:"center"}}>
      <div className="ey rev" style={{justifyContent:"center"}}>System status</div>
      <h1 className="rev d1" style={{fontFamily:"var(--font-fraunces), serif",fontSize:"clamp(32px,4vw,48px)",fontWeight:200,lineHeight:1.1,letterSpacing:"-.03em",marginBottom:"14px"}}>Live platform status.</h1>
      <p style={{fontSize:"14px",color:"rgba(19,18,16,.5)",maxWidth:"480px",margin:"0 auto"}}>Refreshes every 30 seconds. Subscribe to incidents at <a href="https://status.lexvio.ai" style={{color:"var(--cu)"}}>status.lexvio.ai</a>.</p>
    </section>

    <section style={{padding:"8px clamp(20px,4vw,52px) 72px"}}>
      <div style={{maxWidth:"640px",margin:"0 auto"}}>
        {!payload ? (
          <div style={{textAlign:"center",padding:"40px 0",color:"rgba(19,18,16,.45)",fontSize:"14px"}}>Checking status…</div>
        ) : (
          <>
            <div style={{border:"1px solid var(--b200)",borderRadius:"8px",padding:"20px 24px",background:"var(--b50)",display:"flex",alignItems:"center",gap:"16px",marginBottom:"16px"}}>
              <div style={{width:"14px",height:"14px",borderRadius:"50%",background:statusColor(payload.status),boxShadow:`0 0 0 4px ${statusColor(payload.status)}22`,flexShrink:0}} />
              <div style={{flex:1}}>
                <div style={{fontFamily:"var(--font-fraunces), serif",fontSize:"20px",fontWeight:200}}>{statusLabel(payload.status)}</div>
                <div style={{fontSize:"12px",color:"rgba(19,18,16,.45)",marginTop:"2px"}}>Last checked {lastChecked ? lastChecked.toLocaleTimeString() : "—"}{fetching ? " · refreshing" : ""}</div>
              </div>
            </div>

            {payload.checks.length > 0 && (
              <div style={{border:"1px solid var(--b200)",borderRadius:"8px",background:"var(--b50)",marginBottom:"16px"}}>
                {payload.checks.map((c, i) => (
                  <div key={c.name} style={{display:"flex",alignItems:"center",gap:"12px",padding:"14px 20px",borderBottom:i === payload.checks.length - 1 ? "none" : "1px solid var(--b200)"}}>
                    <div style={{width:"10px",height:"10px",borderRadius:"50%",background:c.ok ? "var(--green)" : "var(--red)",flexShrink:0}} />
                    <div style={{fontSize:"14px",textTransform:"capitalize",flex:1}}>{c.name}</div>
                    <div style={{fontSize:"12px",color:"rgba(19,18,16,.5)"}}>
                      {c.ok ? `${c.latency_ms ?? "?"} ms` : (c.error || "failing")}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {payload.version && (
              <div style={{border:"1px solid var(--b200)",borderRadius:"8px",background:"var(--b50)",padding:"16px 20px"}}>
                <div style={{fontSize:"11px",letterSpacing:".14em",textTransform:"uppercase",color:"rgba(19,18,16,.5)",marginBottom:"10px"}}>Current build</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:"10px",fontSize:"13px"}}>
                  <div><span style={{color:"rgba(19,18,16,.45)"}}>Version:</span> <strong>{payload.version.version}</strong></div>
                  <div><span style={{color:"rgba(19,18,16,.45)"}}>Environment:</span> {payload.version.environment}</div>
                  <div><span style={{color:"rgba(19,18,16,.45)"}}>Commit:</span> <code style={{fontSize:"12px"}}>{payload.version.git_sha}</code></div>
                  <div><span style={{color:"rgba(19,18,16,.45)"}}>Deployed:</span> {formatDeployedAgo(payload.version.deployed_at)}</div>
                </div>
              </div>
            )}

            {payload.status === "unreachable" && (
              <div style={{marginTop:"16px",padding:"14px 18px",border:"1px solid var(--red)",borderRadius:"6px",background:"rgba(220,38,38,.06)",fontSize:"13px",color:"var(--red)"}}>
                The status endpoint is not reachable. The marketing site is fine, but the app backend may be offline or the frontend&rsquo;s <code>LEXAI_API_BASE</code> is misconfigured.
              </div>
            )}
          </>
        )}
      </div>
    </section>
  </PageShell></>);
}
